<template>
    <app-page>
        <content-container class="p-1">

            <card v-loading="loading" v-if="group && dashboard" :title="title" :subtitle="subtitle">

                <toolbar class="p-1">
				    <toolbar-group>
					    <toolbar-button title="Go back" @click.prevent="goBack">
						    <icon src="keyboard_backspace" />
					    </toolbar-button>
				    </toolbar-group>

                    <toolbar-group>
					    <toolbar-button title="View dashboard" @click.prevent="gotoDashboard">
						    <icon src="dashboard" />
					    </toolbar-button>
				    </toolbar-group>
			    </toolbar>

                <section class="px-1 pb-1">
                    <form @submit.prevent="save">

				        <form-section label-width="150">

					        <control-group :valid="!$v.dashboard.dashboardName.$error" title="Name">
						        <input
							        v-control
							        v-model.trim="dashboard.dashboardName"
							        @input="$v.dashboard.dashboardName.$touch()"
						        />

						        <template v-if="$v.dashboard.dashboardName.$error">
							        <control-error v-if="!$v.dashboard.dashboardName.unique">That name is already taken</control-error>
							        <control-error v-else-if="!$v.dashboard.dashboardName.required">Choose a name for your dashboard</control-error>
						        </template>
					        </control-group>

                            <control-group :valid="!$v.dashboard.locationGroupID.$error" title="Group">
						        <select v-control v-model="dashboard.locationGroupID" @input="$v.dashboard.locationGroupID.$touch()">
							        <option v-for="group in groups" :key="group.locationGroupID" :value="group.locationGroupID">
                                        {{ group.reportName }}
                                    </option>
						        </select>
                                <template v-if="$v.dashboard.locationGroupID.$error">
							        <control-error v-if="!$v.dashboard.locationGroupID.required">Choose the group this dashboard should belong to</control-error>
						        </template>
					        </control-group>

                            <control-group title="Default user access">
						        <select v-control v-model="dashboard.defaultDashboardPermission">
							        <option v-for="type in dashboardPermissionTypes" :key="type.dashboardPermissionTypeID" :value="type.dashboardPermissionTypeID">
                                        {{ type.dashboardPermissionTypeText }}
                                    </option>
						        </select>
					        </control-group>

                            <control-group title="Default date range" :valid="!$v.dashboard.defaultDateRange.$error || !$v.dashboard.defaultGroupBy.$error">
                                <default-date-range-editor
                                    :date-range="dashboard.defaultDateRange"
                                    :group-by="dashboard.defaultGroupBy"
                                    @change="onDateRangeChange"
                                />

                                <template v-if="$v.dashboard.defaultDateRange.$error || $v.dashboard.defaultGroupBy.$error">
							        <control-error v-if="!$v.dashboard.defaultDateRange.required">Choose a default date range</control-error>
                                    <control-error v-if="!$v.dashboard.defaultGroupBy.required">Choose a default date range grouping</control-error>
						        </template>
                            </control-group>

                            <control-group title="Default historical trend">
						        <select v-control v-model="dashboard.defaultTrendOffset">
							        <option
                                        v-for="type in trendOffsetTypes"
                                        :key="type.trendOffsetTypeID"
                                        :value="type.trendOffsetTypeID"
                                    >
                                        {{type.trendOffsetTypeText}}
                                    </option>
						        </select>
					        </control-group>

                            <control-group inset>
                                <btn primary @click="save">Save</btn>
                                <btn link @click="goBack">Cancel</btn>
                            </control-group>

				        </form-section>			

			        </form>
                </section>

			</card>

        </content-container>

    </app-page>
</template>


<script>
    import _ from 'lodash';
    import { required } from 'validators';
    import { AsyncMixin, FormValidationMixin } from 'pdk/mixins';
    import { notifications } from 'pdk/services';
    import { account, groups, http } from '@/services';
    import { dashboards } from 'dashboard-pdk/services';
    import { actions as dashboardActions } from 'dashboard-pdk/store';
    import { DefaultDateRangeEditor } from 'dashboard-pdk/components/dashboards';


    export default {
        name: 'group-dashboard-editor',
        components: {
            DefaultDateRangeEditor
        },
        mixins: [AsyncMixin, FormValidationMixin],
        beforeRouteEnter(to, from, next) {
            const requests = [
                groups.getGroup({
                    locationGroupID: to.params.locationGroupId
                }),
                dashboards.getDashboard({
                    dashboardID: to.params.dashboardId
                }),
                dashboards.getTrendOffsetTypes(),
                dashboards.getDashboardPermissionTypes(),
                account.getUserGroups({
                    role: 2 // Group admin
                })
            ];

            http.all(requests)
                .then(http.spread((group, dashboard, trendOffsetTypes, dashboardPermissionTypes, groups) => {
                    next((vm) => {
                        vm.group = group;
                        vm.dashboard = dashboard;
                        vm.originalDashboardName = dashboard.dashboardName;
                        vm.trendOffsetTypes = trendOffsetTypes;
                        vm.dashboardPermissionTypes = dashboardPermissionTypes;
                        vm.groups = groups;
                    });
                }));
        },
        beforeRouteUpdate(to, from, next) {
            const requests = [
                groups.getGroup({
                    locationGroupID: to.params.locationGroupId
                }),
                dashboards.getDashboard({
                    dashboardID: to.params.dashboardId
                })
            ];

            http.all(requests)
                .then(http.spread((group, dashboard) => {
                    this.group = group;
                    this.dashboard = dashboard;
                    this.originalDashboardName = dashboard.dashboardName;
                    next();
                }));
        },
        props: {
            locationGroupId: {
                type: String,
                required: true
            },
            dashboardId: {
                type: String,
                required: true
            }
        },
        data() {
            return {
                group: undefined,
                dashboard: undefined,
                groups: [],
                originalDashboardName: null,
                dashboardPermissionTypes: [],
                trendOffsetTypes: []
            };
        },
        validations: {
            dashboard: {
                dashboardName: {
                    required,
                    unique(dashboardName) {
                        if (!dashboardName || !dashboardName.length) return true;
                        if (!this.dashboard) return true;

                        const invalidNames = _(this.dashboards)
                            .map((d) => d.dashboardName.toLowerCase())
                            .filter((n) => n != this.dashboard.dashboardName.toLowerCase())
                            .value();

                        return invalidNames.indexOf(dashboardName.toLowerCase()) === -1;
                    }
                },
                locationGroupID: {
                    required
                },
                defaultDateRange: {
                    required
                },
                defaultGroupBy: {
                    required
                }
            }
		},
        computed: {
            title() {
                return this.originalDashboardName;
            },
            subtitle() {
                return this.group ? this.group.reportName : '';
            }
        },
        methods: {
            getTrendOffsetTypes() {
                return this.async(dashboards.getTrendOffsetTypes())
                    .then((types) => {
                        this.trendOffsetTypes = types;
                    });
            },
            getDashboardPermissionTypes() {
                return this.async(dashboards.getDashboardPermissionTypes())
                    .then((types) => {
                        this.dashboardPermissionTypes = _.filter(types, (l) => !l.isAdmin && !l.isDashboardDefault);
                    });
            },

            onDateRangeChange(data) {
                this.dashboard.defaultDateRange = data.dateRange;
                this.dashboard.defaultGroupBy = data.groupBy;
            },

            gotoDashboard() {
                this.beginAsync();

                this.async(this.$store.dispatch(dashboardActions.NAVIGATE_TO_DASHBOARD, {
                    locationGroupID: this.locationGroupId,
                    dashboardID: this.dashboardId
                }))
                    .then(() => {
                        this.$router.push({
                            name: 'group-dashboards',
                            locationGroupId: this.locationGroupId
                        });
                    });
            },
            save() {
                if (!this.validate()) return;

                this.async(dashboards.updateDashboard(this.dashboard))
                    .then(() => {
                        notifications.open({
                            content: 'Dashboard updated'
                        });

                        this.goBack();
                    })
                    .catch(() => {
                        // TODO - Do something!
                    });
            },
            goBack() {
                this.$router.go(-1);
            }
        }
    };
</script>
